import { CategorySelect } from "@/components/CategorySelect";
import { TransactionTag } from "@/components/TransactionTag";
import { Button } from "@groot/ui/button";
import { Calendar } from "@/app/shared/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/shared/ui/form";
import { Input } from "@groot/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/shared/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/app/shared/ui/sheet";
import { Switch } from "@/app/shared/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@groot/ui/tabs";
import { Textarea } from "@groot/ui/textarea";
import { useDeleteTransaction, useUpdateTransaction } from "@/hooks/api";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import {
  type TransactionFormDataWithValidation,
  transactionFormSchema,
} from "@/schemas/transaction.schema";
import { type Account, type Category, type Tag, type Transaction, TransactionType } from "@/types";
import { json } from "@codemirror/lang-json";
import { zodResolver } from "@hookform/resolvers/zod";
import CodeMirror from "@uiw/react-codemirror";
import dayjs from "dayjs";
import { CalendarIcon, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

interface UpdateTransactionSheetProps {
  transaction: Transaction | null;
  accounts: Account[];
  categories: Category[];
  tags: Tag[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: () => void;
  onDelete?: () => void;
}

export function UpdateTransactionSheet({
  transaction,
  accounts,
  categories,
  tags,
  isOpen,
  onOpenChange,
  onUpdate,
  onDelete,
}: UpdateTransactionSheetProps) {
  const updateMutation = useUpdateTransaction();
  const deleteMutation = useDeleteTransaction();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [jsonValue, setJsonValue] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  const isLoading = updateMutation.isPending || deleteMutation.isPending;

  // Extract category from transaction
  const category = transaction?.category;
  const categoryObject = category?.id ? { label: category.name, value: category.id } : null;

  const form = useForm<TransactionFormDataWithValidation>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: {
      date: transaction?.date ? new Date(transaction.date).toISOString().split("T")[0] : "",
      type: transaction?.type || TransactionType.DEBIT,
      amount: transaction?.amount || 0,
      description: transaction?.description || "",
      payee: transaction?.payee || "",
      merchantName: transaction?.merchantName || "",
      notes: transaction?.notes || "",
      referenceNo: transaction?.referenceNo || "",
      accountId: transaction?.accountId ?? undefined,
      categoryId: categoryObject ? categoryObject.value : null,
      tagIds: transaction?.tags?.map((tag) => tag.id) || [],
      isReviewed: transaction?.isReviewed || false,
      isIncome: transaction?.isIncome || false,
      excludeFromBudget: transaction?.excludeFromBudget || false,
      paymentMode: transaction?.paymentMode || undefined,
      currencyCode: transaction?.currencyCode || "USD",
      tempDescription: transaction?.tempDescription || "",
    },
  });

  useEffect(() => {
    if (transaction && isOpen) {
      const categoryId = transaction.category?.id || null;
      form.reset({
        date: new Date(transaction.date).toISOString().split("T")[0],
        type: transaction.type,
        amount: Number(transaction.amount),
        description: transaction.description || "",
        payee: transaction.payee || "",
        merchantName: transaction.merchantName || "",
        notes: transaction.notes || "",
        referenceNo: transaction.referenceNo || "",
        accountId: transaction.accountId,
        categoryId: categoryId,
        tagIds: transaction.tags?.map((tag) => tag.id) || [],
        isReviewed: transaction.isReviewed || false,
        isIncome: transaction.isIncome || false,
        excludeFromBudget: transaction.excludeFromBudget || false,
        paymentMode: transaction.paymentMode || undefined,
        currencyCode: transaction.currencyCode || "USD",
        tempDescription: transaction.tempDescription || "",
      });
      setJsonValue(JSON.stringify(transaction, null, 2));
      setJsonError(null);
      setIsCategoryLoading(false);
    }
  }, [transaction, isOpen, form]);

  const onSubmit = async (data: TransactionFormDataWithValidation) => {
    if (!transaction) return;

    const payload: TransactionFormDataWithValidation = {
      ...data,
      ...(transaction.isReviewed === false ? { isReviewed: true } : {}),
    };

    updateMutation.mutate(
      { id: transaction.id, data: payload },
      {
        onSuccess: () => {
          onOpenChange(false);
          onUpdate?.();
        },
      },
    );
  };

  const handleDelete = async () => {
    if (!transaction) return;

    deleteMutation.mutate(transaction.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        onOpenChange(false);
        onDelete?.();
      },
    });
  };

  const handleJsonUpdate = () => {
    if (!transaction) return;

    try {
      const parsedData = JSON.parse(jsonValue);
      setJsonError(null);

      // Convert the parsed JSON to the format expected by the API
      const updateData: Partial<Transaction> = {
        date: parsedData.date,
        type: parsedData.type,
        amount: parsedData.amount,
        description: parsedData.description,
        payee: parsedData.payee,
        merchantName: parsedData.merchantName,
        notes: parsedData.notes,
        referenceNo: parsedData.referenceNo,
        accountId: parsedData.accountId,
        categoryId: parsedData.categoryId,
        isReviewed: transaction.isReviewed === false ? true : parsedData.isReviewed,
        isIncome: parsedData.isIncome,
        excludeFromBudget: parsedData.excludeFromBudget,
        paymentMode: parsedData.paymentMode,
        currencyCode: parsedData.currencyCode,
        tempDescription: parsedData.tempDescription,
      };

      updateMutation.mutate(
        {
          id: transaction.id,
          data: updateData as TransactionFormDataWithValidation,
        },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Transaction updated successfully",
            });
            onOpenChange(false);
            onUpdate?.();
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: error.message || "Failed to update transaction",
              variant: "destructive",
            });
          },
        },
      );
    } catch {
      setJsonError("Invalid JSON format");
      toast({
        title: "Error",
        description: "Invalid JSON format. Please check your syntax.",
        variant: "destructive",
      });
    }
  };

  if (!transaction) return null;

  const selectedAccount = accounts.find((acc) => acc.id === transaction.accountId);

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(value) => {
          if (!value) {
            form.reset();
          }
          onOpenChange(value);
        }}
      >
        <SheetContent className="overflow-y-auto bg-background w-[400px] sm:w-[540px]">
          <Form {...form}>
            <SheetHeader>
              <SheetTitle>
                <div className="flex justify-between items-center">
                  <span>Update Transaction</span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="outline"
                      className="size-6"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="size-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-6"
                      onClick={() => onOpenChange(false)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                </div>
              </SheetTitle>
              <SheetDescription asChild>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {selectedAccount?.iconUrl && (
                    <img
                      src={selectedAccount.iconUrl}
                      alt={selectedAccount.accountName}
                      className="size-5 rounded object-cover"
                    />
                  )}
                  <span>
                    {selectedAccount?.accountName} - {selectedAccount?.lastFourAccountNumber}
                  </span>
                  <span className="text-muted-foreground">
                    • ID:{" "}
                    <a
                      href={`https://supabase.com/dashboard/project/vxavyhxpqzbwtzaapkio/editor/17813?schema=public&filter=id%3Aeq%3A${transaction.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline text-info"
                    >
                      {transaction.id}
                    </a>
                  </span>
                </div>
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 border-b -mx-6" />

            <Tabs defaultValue="form" className="mt-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="form">Form</TabsTrigger>
                <TabsTrigger value="json">Raw JSON</TabsTrigger>
              </TabsList>

              <TabsContent value="form" className="mt-6">
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-5 gap-4">
                    <FormField
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col col-span-3">
                          <FormLabel>Date</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "w-full text-left font-normal",
                                    !field.value && "text-muted-foreground",
                                  )}
                                >
                                  {field.value ? (
                                    dayjs(field.value).format("MMM DD, YYYY")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto size-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                onSelect={(date) =>
                                  date && field.onChange(date.toISOString().split("T")[0])
                                }
                                disabled={(date) =>
                                  date > new Date() || date < new Date("1900-01-01")
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="amount"
                      render={({ field }) => (
                        <FormItem className="flex flex-col col-span-2">
                          <FormLabel>Amount</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Amount"
                              type="number"
                              step="0.01"
                              {...field}
                              onChange={(e) =>
                                field.onChange(Number.parseFloat(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Input placeholder="Add a description" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="categoryId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category</FormLabel>
                          <CategorySelect
                            categories={categories}
                            value={
                              typeof field.value === "number"
                                ? {
                                    label: categories.find((c) => c.id === field.value)?.name || "",
                                    value: field.value,
                                  }
                                : null
                            }
                            onChange={(newValue) => {
                              field.onChange(newValue ? newValue.value : null);
                            }}
                            inputRef={field.ref}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select transaction type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={TransactionType.CREDIT}>Credit</SelectItem>
                              <SelectItem value={TransactionType.DEBIT}>Debit</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="tempDescription"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Temp Description</FormLabel>
                          <FormControl>
                            <Input placeholder="Temp description" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="referenceNo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reference No.</FormLabel>
                          <FormControl>
                            <Input placeholder="Reference no." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="tagIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <TransactionTag
                          options={tags}
                          transactionId={transaction.id}
                          inputRef={field.ref}
                          isCategoryLoading={isCategoryLoading}
                          value={
                            field.value
                              ?.map((id) => tags.find((t) => t.id === id))
                              .filter(Boolean) as Tag[]
                          }
                          onChange={(newTags) => field.onChange(newTags?.map((t) => t.id) || [])}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Add a note..."
                            className="min-h-[100px] resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <FormField
                        control={form.control}
                        name="isReviewed"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="text-sm font-normal">Reviewed</FormLabel>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="excludeFromBudget"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="text-sm font-normal">
                              Exclude from Budget
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button type="submit" size="lg" disabled={isLoading}>
                        {isLoading ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="json" className="mt-6">
                <div className="space-y-4">
                  {jsonError && (
                    <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded">
                      {jsonError}
                    </div>
                  )}
                  <div className="border rounded-md overflow-hidden">
                    <CodeMirror
                      value={jsonValue}
                      height="500px"
                      extensions={[json()]}
                      onChange={(value) => setJsonValue(value)}
                      theme="light"
                      basicSetup={{
                        lineNumbers: true,
                        foldGutter: true,
                        highlightActiveLineGutter: true,
                        highlightActiveLine: true,
                      }}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleJsonUpdate} size="lg" disabled={isLoading}>
                      {isLoading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </Form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transaction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isLoading}>
              {isLoading ? "Deleting..." : "Delete Transaction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
